import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { RARITY_META } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Flame, Trophy, MapPin, Award, Globe, Star, Sparkles, Layers, Swords, Crown, ChevronRight, Camera, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { ObsImage } from './Dashboard';
import { RARITY_UI } from '../lib/rarity';
import RarityBadge from '../components/RarityBadge';
import InviteCard from '../components/InviteCard';
import NicknameEditor from '../components/NicknameEditor';
import HabitatsCard from '../components/HabitatsCard';
import FieldGuideCard from '../components/FieldGuideCard';
import DigestPreferenceCard from '../components/DigestPreferenceCard';
import CycleCountdown from '../components/CycleCountdown';
import MyFieldTeamCarousel from '../components/ar/MyFieldTeamCarousel';

const RANK_COLORS = { Bronze: '#B08B58', Silver: '#C0C0C0', Gold: '#E9C46A', Platinum: '#8ECAE6', Diamond: '#FF6EC7' };

export default function Profile() {
  const { user, checkAuth } = useAuth();
  const nav = useNavigate();
  const [obs, setObs] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [legacy, setLegacy] = useState(null);
  const [battleStats, setBattleStats] = useState(null);
  const [decks, setDecks] = useState([]);
  const [nickname, setNickname] = useState(user?.nickname || null);

  useEffect(() => {
    api.get('/observations?limit=30').then(r => setObs(r.data)).catch(() => {});
    api.get('/achievements').then(r => setAchievements(r.data)).catch(() => {});
    api.get('/legacy').then(r => setLegacy(r.data)).catch(() => {});
    api.get('/battle-stats/me').then(r => setBattleStats(r.data)).catch(() => {});
    api.get('/decks').then(r => setDecks(r.data.decks || [])).catch(() => {});
    api.get('/subscription/status').then(r => setNickname(r.data?.nickname || null)).catch(() => {});
  }, []);

  if (!user) return null;
  const unlocked = achievements.filter(a => a.unlocked);

  return (
    <div className="space-y-6" data-testid="profile-root">
      {/* Hero */}
      <div className="text-center">
        <ProfilePictureEditor
          currentPicture={user.picture}
          founder={!!user.is_founder}
          onChanged={() => checkAuth?.()}
        />
        <h2 className="font-serif text-3xl mt-3">{user.name}</h2>
        {user.is_founder && (
          <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold tracking-widest uppercase"
               style={{ color: '#E9C46A' }}
               data-testid="profile-founder-badge">
            <span aria-hidden>★</span> Founder
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-1">{user.email}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-2">
        <BigStat label="XP" value={(user.xp || 0).toLocaleString()} icon={Trophy} />
        <BigStat label="Species" value={user.species_count || 0} icon={MapPin} />
        <BigStat label="Streak" value={`${user.streak || 0}d`} icon={Flame} />
        <BigStat label="Top" value={user.top_discovery_score || 0} icon={Award} />
      </div>

      {/* Explorer identity — nickname (90-day cooldown enforced server-side) */}
      <NicknameEditor
        currentNickname={nickname || user.nickname}
        onChanged={(nick) => { setNickname(nick); checkAuth?.(); }}
      />

      {/* Cycle countdown — reassures free explorers when the next 5 credits arrive */}
      <CycleCountdown />

      {/* Invite loop — earn WildCoins + Trail Blazer badge */}
      <InviteCard />

      {/* Field-Team AR photos carousel */}
      <MyFieldTeamCarousel />

      {/* Habitat expedition progress + tier badges (iter39). */}
      <HabitatsCard />

      {/* Field Guide PDF export (iter62). */}
      <FieldGuideCard />

      {/* Weekly digest email preferences (iter68). */}
      <DigestPreferenceCard />

      {/* Battle Statistics — one of the five pillars */}
      <BattleStatsSection stats={battleStats} decks={decks} onOpen={() => nav('/battle/profile')} onOpenHistory={() => nav('/battle/history')} />

      {/* Collections shortcut (Collect no longer in bottom nav) */}
      <button onClick={() => nav('/collections')} data-testid="profile-collections-shortcut"
        className="w-full rounded-2xl bg-card border border-white/5 p-3 flex items-center gap-3 hover:border-accent/40">
        <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center"><Layers className="w-5 h-5 text-primary-light" strokeWidth={1.8} /></div>
        <div className="flex-1 text-left">
          <p className="font-serif text-base">Collections</p>
          <p className="text-[10px] text-muted-foreground">Browse your discoveries by category</p>
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground" />
      </button>

      {/* Explorer Legacy — permanent lifetime stats */}
      {legacy && (
        <section data-testid="legacy-section">
          <h3 className="text-[10px] uppercase tracking-[0.25em] text-accent mb-3">Explorer Legacy</h3>
          <div className="rounded-3xl bg-card border border-white/5 p-4 space-y-4">
            <div className="grid grid-cols-3 gap-2">
              <LegacyStat icon={Sparkles} label="Lifetime Score" value={legacy.lifetime_discovery_score?.toLocaleString?.() || 0} accent="#E9C46A" />
              <LegacyStat icon={Globe} label="World Firsts" value={legacy.world_firsts} accent="#FF6EC7" />
              <LegacyStat icon={Star} label="Regional Firsts" value={legacy.regional_firsts} accent="#8ECAE6" />
              <LegacyStat icon={Star} label="Seasonal Firsts" value={legacy.seasonal_firsts} accent="#95D5B2" />
              <LegacyStat icon={Layers} label="Battle Cards" value={legacy.collectibles_earned} />
              <LegacyStat icon={Award} label="Level" value={legacy.level} />
            </div>

            {/* Rarity breakdown */}
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Discoveries by rarity</p>
              <div className="grid grid-cols-6 gap-1.5" data-testid="legacy-rarity-grid">
                {['common','uncommon','rare','epic','legendary','mythic'].map(r => (
                  <div key={r} className="rounded-lg bg-black/30 border border-white/5 py-2 text-center">
                    <p className="font-mono text-sm tabular-nums" style={{ color: RARITY_META[r]?.color || '#9CA3AF' }}>{legacy.rarity_counts?.[r] || 0}</p>
                    <p className="text-[8px] uppercase tracking-widest text-muted-foreground truncate">{r.slice(0, 5)}</p>
                  </div>
                ))}
              </div>
            </div>

            {legacy.top_habitats?.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Favourite habitats</p>
                <div className="space-y-1.5">
                  {legacy.top_habitats.slice(0, 3).map(h => (
                    <div key={h.area} className="flex items-center justify-between text-xs">
                      <span className="truncate">{h.area}</span>
                      <span className="font-mono text-accent">{h.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Achievements */}
      {achievements.length > 0 && (
        <section>
          <h3 className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">Achievements ({unlocked.length}/{achievements.length})</h3>
          <div className="grid grid-cols-4 gap-2">
            {achievements.slice(0, 12).map(a => (
              <div key={a.code} className={`rounded-2xl aspect-square bg-card border flex flex-col items-center justify-center p-2 text-center ${a.unlocked ? 'border-accent/40' : 'border-white/5 opacity-40'}`} data-testid={`ach-${a.code}`}>
                <Award className={`w-6 h-6 mb-1 ${a.unlocked ? 'text-accent' : 'text-muted-foreground'}`} strokeWidth={1.5} />
                <p className="text-[9px] leading-tight">{a.title}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Recent */}
      <section>
        <h3 className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">Your Discoveries</h3>
        {obs.length === 0 ? (
          <div className="rounded-3xl bg-card border border-white/5 p-8 text-center text-sm text-muted-foreground">No discoveries yet. Tap the camera to begin!</div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {obs.map(o => (
              <div key={o.observation_id} className={`rounded-2xl aspect-square overflow-hidden bg-card border border-white/5 relative`}>
                {o.image_path ? <ObsImage path={o.image_path} /> : <div className="w-full h-full bg-gradient-to-br from-primary/30 to-primary/5" />}
                <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded-full bg-black/60 backdrop-blur text-[9px] font-mono" style={{ color: RARITY_META[o.species_rarity]?.color }}>{o.discovery_score}</div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function BigStat({ label, value, icon: Icon }) {
  return (
    <div className="rounded-2xl bg-card border border-white/5 p-2 text-center">
      <Icon className="w-4 h-4 mx-auto text-accent" strokeWidth={1.75} />
      <p className="font-mono font-semibold text-sm mt-1 tabular-nums truncate">{value}</p>
      <p className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</p>
    </div>
  );
}

function LegacyStat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl bg-black/30 border border-white/5 p-2 text-center">
      <Icon className="w-3.5 h-3.5 mx-auto mb-0.5" strokeWidth={1.75} style={{ color: accent || '#9CA3AF' }} />
      <p className="font-mono font-semibold text-base tabular-nums" style={{ color: accent }}>{value}</p>
      <p className="text-[8px] uppercase tracking-widest text-muted-foreground leading-tight">{label}</p>
    </div>
  );
}

function BattleStatsSection({ stats, decks, onOpen, onOpenHistory }) {
  const rankColor = RANK_COLORS[stats?.rank?.tier] || '#E9C46A';
  const fav = stats?.favorite_card;
  const favUI = fav ? (RARITY_UI[fav.rarity] || RARITY_UI.common) : null;
  return (
    <section data-testid="profile-battle-stats">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[10px] uppercase tracking-[0.25em] text-accent flex items-center gap-1"><Swords className="w-3 h-3" /> Battle Statistics</h3>
        <button onClick={onOpen} data-testid="profile-open-battle-profile" className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground">View arena ›</button>
      </div>
      <div className="rounded-3xl bg-card border border-white/5 p-4 space-y-4">
        {/* Rating / rank strip */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 border-2"
            style={{ borderColor: rankColor, background: `${rankColor}18` }}>
            <Crown className="w-6 h-6" style={{ color: rankColor }} strokeWidth={1.6} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Battle Rating</p>
            <div className="flex items-baseline gap-2 mt-0.5">
              <p className="font-mono text-2xl tabular-nums" style={{ color: rankColor }} data-testid="profile-battle-rating">{stats?.rating ?? 1000}</p>
              <p className="text-xs" style={{ color: rankColor }} data-testid="profile-battle-rank">{stats?.rank?.label || 'Unranked'}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2">
          <MiniStat testid="profile-battle-wins" label="Wins" value={stats?.wins ?? 0} accent="#4ade80" />
          <MiniStat testid="profile-battle-losses" label="Losses" value={stats?.losses ?? 0} accent="#f87171" />
          <MiniStat testid="profile-battle-winrate" label="Win %" value={`${stats?.win_rate ?? 0}%`} />
          <MiniStat testid="profile-battle-total" label="Total" value={stats?.total_battles ?? 0} />
        </div>

        {/* Favorite Card */}
        {fav ? (
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Favorite card</p>
            <div className={`rounded-2xl p-3 ${favUI.cardClass}`} style={favUI.cardStyle} data-testid="profile-battle-fav-card">
              <div className="flex items-center gap-2">
                <RarityBadge rarity={fav.rarity} size="sm" showParticles={false} />
                <p className="font-serif text-sm truncate flex-1">{fav.species_common_name}</p>
                <span className="font-mono text-[10px] text-emerald-400">{fav.wins_with_card}W</span>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-[10px] text-muted-foreground">No favorite card yet — win a match to establish one.</p>
        )}

        {/* Battle Deck */}
        <div>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Battle deck</p>
          {decks.length === 0 ? (
            <p className="text-[11px] text-muted-foreground">No decks yet.</p>
          ) : (
            <p className="text-xs" data-testid="profile-battle-deck-summary">
              <span className="font-serif">{decks[0].name}</span>
              <span className="text-muted-foreground"> · {decks[0].card_ids?.length || 0} cards · {decks[0].consumable_ids?.length || 0} consumables · {decks[0].equipment_ids?.length || 0} equipment</span>
            </p>
          )}
        </div>

        <button onClick={onOpenHistory} data-testid="profile-battle-history-btn"
          className="w-full py-2.5 rounded-full bg-white/5 hover:bg-white/10 text-[10px] uppercase tracking-widest">
          View Battle History
        </button>
      </div>
    </section>
  );
}

function MiniStat({ label, value, accent, testid }) {
  return (
    <div className="rounded-xl bg-black/30 border border-white/5 p-2 text-center" data-testid={testid}>
      <p className="font-mono font-semibold text-base tabular-nums" style={{ color: accent }}>{value}</p>
      <p className="text-[9px] uppercase tracking-widest text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}

// Inline profile-picture editor. Hover reveals the "change" chip; clicking
// it opens a hidden <input type=file> that streams straight to
// POST /api/profile/picture. New URL replaces the avatar without a full
// page reload.
function ProfilePictureEditor({ currentPicture, onChanged, founder = false }) {
  const inputRef = useRef(null);
  const [picture, setPicture] = useState(currentPicture);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (f) => {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error('Picture too large (max 5MB)');
      return;
    }
    if (!/^image\/(jpe?g|png|webp|gif)$/i.test(f.type)) {
      toast.error('Use JPG, PNG, WEBP or GIF');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', f);
      const { data } = await api.post('/profile/picture', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPicture(data.picture);
      toast.success('Explorer picture updated');
      onChanged?.(data.picture);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const src = picture || 'https://i.pravatar.cc/150';

  return (
    <div className="relative inline-block" data-testid={founder ? 'profile-picture-founder-wrap' : 'profile-picture-wrap'}>
      {founder ? (
        <span
          className="w-24 h-24 rounded-full mx-auto block relative"
          style={{
            padding: 3,
            background:
              'conic-gradient(from 210deg, #E9C46A 0deg, #F9D976 90deg, #B8860B 180deg, #F9D976 270deg, #E9C46A 360deg)',
            boxShadow:
              '0 0 0 1px rgba(233,196,106,0.35), 0 0 18px rgba(233,196,106,0.35)',
          }}
        >
          <img
            src={src}
            alt=""
            className="w-full h-full rounded-full object-cover block"
            style={{ background: '#040A08' }}
            data-testid="profile-picture-img"
          />
        </span>
      ) : (
        <img
          src={src}
          alt=""
          className="w-24 h-24 rounded-full mx-auto object-cover ring-4 ring-primary/30"
          data-testid="profile-picture-img"
        />
      )}
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        data-testid="profile-picture-edit-btn"
        aria-label="Change profile picture"
        className="absolute bottom-0 right-0 w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center border-2 border-[#040A08] shadow-lg hover:bg-primary/80 disabled:opacity-60"
      >
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={e => handleFile(e.target.files?.[0])}
        className="hidden"
        data-testid="profile-picture-file-input"
      />
    </div>
  );
}
